import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {CircularProgress, Divider} from "@mui/material";
import {useUpdateEffect} from "react-use";
import useIsMounted from "../../../../../../../../../hooks/use-is-mounted";
import {
    DataGridActionTypes,
    DataGridColumnAlignments,
    DataGridColumnTypes,
    DataGridColumnWidthTypes,
    DataGridExoticColumnFields,
    useDataGridSavedState
} from "../../../../../../../../../../packages/data-grid";
import {FreeShippingFeesManagementOrderSimulationsDataGridColumnNames} from "../../../../../../../../../../core/constants/table/data-grid-column-keys";
import {OutlinedInput} from "../../../../../../../../../components/base/input/outlined_input";
import PartnerDataGrid from "../../../../../../../../../containers/data-grid";
import classnames from "classnames";
import {Close, Done, Info, Refresh} from "@mui/icons-material";
import Api from "../../../../../../../../../../core/services/api/api";
import {Col} from "reactstrap";
import {OutlinedDropdown} from "../../../../../../../../../components/base/dropdown/outlined_dropdown";
import IconButton from "../../../../../../../../../components/base/button/icon_button";
import FreeFreightEligibilityFullDetailsDialog from "../../../../../../../../../components/dialogs/free-freight-eligibility-full-details";
import ApiResponseCodes from "../../../../../../../../../../core/models/static/api-response-codes";
import {DatePicker} from "@mui/x-date-pickers";
import DateUtils from "../../../../../../../../../../core/services/date-utils";
import useCachedPriceLevelsDictionaryController from "../../../../../../../../../hooks/use-price-level-dictionary";
import CustomerAutocomplete from "../../../../../../../../../components/text-fields/autocomplete/customer-autocomplete";
import {useAppSelector} from "../../../../../../../../../redux";
import axios from "axios";
import useAccessLevels from "../../../../../../../../../hooks/use-access-levels";
import PartAutocomplete from "../../../../../../../../../components/text-fields/autocomplete/part-autocomplete";

const eligibilityStates = {
    undetermined: 'undetermined',
    warning: 'warning',
    approved: 'approved',
    denied: 'denied',
}

const dataGridSaveConfig = {
    name: 'FreeShippingFeesManagementViewSettingsOrderSimulationDataGrid',
    version: 1,
}


/**
 * The available Actions for the free shipping fees management view
 */
const FreeShippingFeesManagementViewOrderSimulationDataGridActionNames = {
    Delete: 'delete'
}

const initEligibilityState = {state: eligibilityStates.undetermined, message: [], data: null};
const initFreeFreightEligibilityFullDetailsDialogState = {open: false, data: null};

const FreeShippingFeesManagementViewSettingsOrderSimulation = ({refreshExceptions}) => {
    const operatorAccessLevels = useAccessLevels();
    const defaultCustomer = useAppSelector(state => state.customerInformation);
    const [selectedCustomer, setSelectedCustomer] = useState(defaultCustomer?.id ? defaultCustomer : null);

    const [isSelectedCustomerDetached, setIsSelectedCustomerDetached] = useState(false);
    const [userAccountInfo, setUserAccountInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [deliveryDate, setDeliveryDate] = useState(DateUtils.UTCNow());
    const [freightCompany, setFreightCompany] = useState(null);
    const [freightCompanies, setFreightCompanies] = useState([]);
    const [items, setItems] = useState([]);
    const [refresher, setRefresher] = useState(false);
    const [eligibilityState, setEligibilityState] = useState(initEligibilityState);
    const [freeFreightEligibilityFullDetailsDialogState, setFreeFreightEligibilityFullDetailsDialogState] = useState(initFreeFreightEligibilityFullDetailsDialogState);
    const isMounted = useIsMounted();
    /**@type {React.MutableRefObject<DataGridController>}*/
    const dataGridController = useRef();
    const {getPriceLevelDictionary} = useCachedPriceLevelsDictionaryController();

    /**
     * With each change in the [defaultCustomer?.id]'s value:
     * - Sets the selected customer to the default customer if the default customer is not null.
     */
    useEffect(() => {
        setSelectedCustomer(defaultCustomer?.id ? defaultCustomer : null);
    }, [defaultCustomer])

    const existingPartIds = useMemo(() => items.map(e => e.partId), [items])

    const columns = useMemo(() =>
            /** @type {DataGridColumn[]}*/
            [
                {
                    name: DataGridExoticColumnFields.selection,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    resizable: false,
                    visibilityToggleable: false,
                    pinnedToggleable: false,
                    reorderable: false,
                    sortable: false,
                    pinned: false,
                },
                {
                    title: "Product",
                    name: FreeShippingFeesManagementOrderSimulationsDataGridColumnNames.product,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                    width: {
                        size: 1,
                        type: DataGridColumnWidthTypes.flex,
                    },
                },
                {
                    title: "Category",
                    name: FreeShippingFeesManagementOrderSimulationsDataGridColumnNames.category,
                    type: DataGridColumnTypes.string,
                    sortable: false,
                    width: {
                        size: 1,
                        type: DataGridColumnWidthTypes.flex,
                    },
                },
                {
                    title: "Quantity",
                    name: FreeShippingFeesManagementOrderSimulationsDataGridColumnNames.quantity,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    width: {
                        size: 1,
                        type: DataGridColumnWidthTypes.flex,
                    },
                },
                {
                    title: "Price",
                    name: FreeShippingFeesManagementOrderSimulationsDataGridColumnNames.price,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    width: {
                        size: 1,
                        type: DataGridColumnWidthTypes.flex,
                    },
                },
            ],
        [])

    // eslint-disable-next-line no-unused-vars
    const [dataGridColumns, _, __, dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    })

    /**
     * Adds a new item in the list of order simulated items.
     * @type {(function(*): Promise<void>)|*}
     */
    const addItem = useCallback(async (product) => {
        let price = 0;
        if (!!selectedCustomer?.id) {
            // TODO: call api for getting a product's price for the selected user id.
        }
        const partInfoResponse = await Api.getPartInfoWithPartNumber({
            partNumbers: [product.partNo],
        })
        if (!isMounted() || !partInfoResponse?.resultFlag)
            return;
        const partData = partInfoResponse.data[0];
        setItems(prevState => {
            const existingItem = prevState.find(e => e.originalPartId === product.id);
            if (existingItem) {
                return prevState.map(e => {
                    if (e.originalPartId === product.id) {
                        const quantity = operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders ? parseFloat(e.quantity) : parseInt(e.quantity)
                        return {
                            ...e,
                            quantity: (quantity + 1).toString(),
                        }
                    }
                    return e;
                });
            }
            return [
                {
                    originalPartId: product.id,
                    originalPartNo: product.partNo,
                    originalParentCategoryId: partData.parentCategoryId,
                    originalParentCategoryTitle: partData.parentCategoryTitle,
                    quantity: '1',
                    price: price.toString(),
                }
                , ...prevState
            ]
        })
    }, [isMounted, selectedCustomer?.id, operatorAccessLevels.packages.sales.allowDecimalQuantityInOrders])

    /**
     * Updates the price of the items that are already in the list of order simulated items based on the new selected user.
     * @type {(function(*): Promise<void>)|*}
     */
    const updatePricesOfExistingItems = useCallback(async (selectedCustomerId) => {
        // TODO: call api for fetching the products' price for the selected user id.
        const response = {
            resultFlag: true,
            data: Object.fromEntries(items.map((e) => [e.partId, e.price])),
        }
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setItems(prevState => prevState.map(e => ({...e, price: (response.data[e.partId] ?? e.price).toString()})))
        }
    }, [isMounted])

    /**
     * Determines if the selected customer is detached or not.
     * @type {(function(*): Promise<undefined|void>)|*}
     */
    const determineIfSelectedCustomerDetached = useCallback(async (selectedCustomerId) => {
        if (!selectedCustomerId)
            return;
        const response = await Api.getFreightCustomerExceptionInfo(selectedCustomerId);
        if (!isMounted())
            return;
        if (response.errorCode === ApiResponseCodes.freeFreightCustomerNotDetached) {
            return setIsSelectedCustomerDetached(false);
        } else {
            setIsSelectedCustomerDetached(true);
        }
    }, [isMounted])

    /**
     * With each change in the [selectedUser?.customer?.id]'s value:
     * - If the [selectedUser?.customer?.id] is not null, then the prices of the existing items will be updated.
     */
    useEffect(() => {
        if (selectedCustomer?.id) {
            void updatePricesOfExistingItems(selectedCustomer?.id);
        }
        void determineIfSelectedCustomerDetached(selectedCustomer?.id);
    }, [determineIfSelectedCustomerDetached, selectedCustomer?.id, updatePricesOfExistingItems])

    /**
     * Simulates the free shipping eligibility for the selected user and the items in the list of order simulated items.
     * @type {(function(*): Promise<void|undefined>)}
     */
    const determineFreeShippingEligibility = useCallback(async (cancelToken) => {
        if (!freightCompany?.id) {
            return setEligibilityState({
                state: eligibilityStates.warning,
                data: null,
                message: ["You must select a freight company to simulate free shipping eligibility"]
            })
        }
        if (!deliveryDate) {
            return setEligibilityState({
                state: eligibilityStates.warning,
                data: null,
                message: ["You must select a delivery date to simulate free shipping eligibility"]
            })
        }
        if (!selectedCustomer?.id) {
            return setEligibilityState({
                state: eligibilityStates.warning,
                data: null,
                message: ["You must select a customer to simulate free shipping eligibility"]
            })
        }
        if (!userAccountInfo?.priceLevel?.id) {
            return setEligibilityState({
                state: eligibilityStates.warning,
                data: null,
                message: ["The selected customer is not assigned to a price level to simulate free shipping eligibility"]
            })
        }
        if (!items.length && eligibilityState.state !== eligibilityStates.undetermined) {
            return setEligibilityState({
                state: eligibilityStates.warning,
                data: null,
                message: ["You must add at least one product to simulate free shipping eligibility"]
            })
        }
        if (items.length && !items.some(e => !Number.isNaN(Number(e.price)) && Number(e.price) > 0)) {
            return setEligibilityState({
                state: eligibilityStates.warning,
                data: null,
                message: ["At least one item must have a price to simulate free shipping eligibility"]
            })
        }
        if (items.length && !items.some(e => !Number.isNaN(Number(e.quantity)) && Number(e.quantity) > 0)) {
            return setEligibilityState({
                state: eligibilityStates.warning,
                data: null,
                message: ["At least one item must have a valid quantity to simulate free shipping eligibility"]
            })
        }
        if (!items.length)
            return;
        setLoading(true);
        const forApi = {
            originalRegionId: userAccountInfo?.region?.id,
            freightCompanyId: freightCompany.id,
            originalPriceLevelId: userAccountInfo?.priceLevel?.id,
            orderDate: deliveryDate ? DateUtils.UTCToServer(deliveryDate).format(DateUtils.withoutOffsetFormat) : null,
            originalCustomerId: selectedCustomer?.id,
            parts: items.map(e => ({...e, price: Number(e.price), quantity: Number(e.quantity)})),
        }
        const response = await Api.simulateFreeFreight(forApi, cancelToken);
        setLoading(false);
        if (!isMounted())
            return;
        if (!response.resultFlag) {
            return setEligibilityState(initEligibilityState);
        }
        return setEligibilityState({
            state: response.data.isFree ? eligibilityStates.approved : eligibilityStates.denied,
            data: response.data,
            message: response.data?.friendlyReason?.split('\n')?.filter(e => !!e) ?? [],
        })
    }, [eligibilityState.state, isMounted, items, selectedCustomer?.id, deliveryDate, freightCompany?.id, userAccountInfo?.priceLevel?.id, userAccountInfo?.region?.id])

    /**
     * With each change in the dependencies of [determineFreeShippingEligibility]'s function:
     * - determines the free shipping eligibility.
     */
    useUpdateEffect(() => {
        const axiosCancelToken = axios.CancelToken.source();
        const timer = setTimeout(() => {
            void determineFreeShippingEligibility(axiosCancelToken.token);
        }, 500)
        return () => {
            axiosCancelToken.cancel();
            clearTimeout(timer);
        }
    }, [determineFreeShippingEligibility])

    /**
     * With each change in the [refresher]:
     * - determines the free shipping eligibility.
     */
    useUpdateEffect(() => {
        const axiosCancelToken = axios.CancelToken.source();
        void determineFreeShippingEligibility(axiosCancelToken.token);
        return () => {
            axiosCancelToken.cancel();
        }
    }, [refresher])

    /**
     * Updates the price of the item with the given part id.
     * @type {(function(*, *): void)|*}
     */
    const updateItemPrice = useCallback((partId, price) => {
        setItems(prevState => prevState.map(e => e.originalPartId === partId ? {...e, price: price ?? ''} : e))
    }, [])

    /**
     * Updates the quantity of the item with the given part id.
     * @type {(function(*, *): void)|*}
     */
    const updateItemQuantity = useCallback((partId, quantity) => {
        setItems(prevState => prevState.map(e => e.originalPartId === partId ? {...e, quantity: quantity ?? ''} : e))
    }, [])

    /**
     * Removes the items with the given part ids from the list of order simulated items.
     * @type {(function(*): void)|*}
     */
    const removeItems = useCallback((partIds) => {
        setItems(prevState => prevState.filter(e => !partIds.includes(e.originalPartId)));
        dataGridController.current.resetSelection();
    }, [])

    /**
     * Handles the action invoked event.
     * @type {(function(*, *, *, *, *): Promise<void>)|*}
     */
    const onActionInvoked = useCallback(async (action, selectedRows, excludedRows, allRowsSelected, pagination) => {
        switch (action.name) {
            case FreeShippingFeesManagementViewOrderSimulationDataGridActionNames.Delete: {
                if (!allRowsSelected) {
                    // not all rows are selected, therefore, the selectedRows list is not empty
                    return removeItems(selectedRows.map(e => e.data.originalPartId));
                }
                if (pagination.pageSize >= pagination.length) {
                    // all the items are within a single page, so we have the data in hand
                    return removeItems(items.map(e => e.originalPartId));
                }
                removeItems(items.map(e => e.originalPartId));
                break;
            }
            default:
                break;
        }
    }, [items, removeItems])

    /**
     * Fetches the user account information (price level and region) for the selected user.
     * @type {(function(): Promise<void>)}
     */
    const getCustomerAccountInfo = useCallback(async () => {
        if (!selectedCustomer?.id)
            return;
        const [customerPriceLevelsResponse, customerRegionResponse, priceLevelDict] = await Promise.all([
            Api.getCustomerPriceLevels(selectedCustomer.id),
            Api.getCustomerRegion({customerId: selectedCustomer.id}),
            getPriceLevelDictionary(),
        ]);
        if (!isMounted())
            return;
        if (customerPriceLevelsResponse?.resultFlag) {
            setUserAccountInfo(prevState => {
                const found = customerPriceLevelsResponse?.data?.find(e => !e.partCategoryId);
                const priceLevel = found ? {id: found.priceLevelId, title: found.priceLevelTitle} : null;
                if (priceLevel) {
                    priceLevel.title = priceLevelDict?.find(e => e.priceLevelId === priceLevel.id)?.title ?? priceLevel.title;
                }
                return ({
                    ...(prevState ?? {}),
                    priceLevel: priceLevel ? {...priceLevel} : priceLevel,
                })
            });
        } else {
            setUserAccountInfo(prevState => ({
                ...(prevState ?? {}),
                priceLevel: null
            }));
        }
        if (customerRegionResponse?.resultFlag) {
            setUserAccountInfo(prevState => ({...(prevState ?? {}), region: customerRegionResponse.data}));
        } else {
            setUserAccountInfo(prevState => ({
                ...(prevState ?? {}),
                region: null
            }));
        }
    }, [selectedCustomer?.id, getPriceLevelDictionary, isMounted])

    /**
     * With each change in the dependencies of [getUserAccountInfo]'s function:
     * - fetches the user account information (price level and region) for the selected user.
     */
    useEffect(() => {
        void getCustomerAccountInfo();
    }, [getCustomerAccountInfo])

    /**
     * Fetches the freight companies list from the server.
     * @type {(function(): Promise<void>)}
     */
    const getFreightCompanies = useCallback(async () => {
        const response = await Api.getFreightCompanies({});
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setFreightCompanies(response.data);
        }
    }, [isMounted])

    /**
     * With each change in the dependencies of [getFreightCompanies]'s function:
     * - fetches the freight companies list from the server.
     */
    useEffect(() => {
        void getFreightCompanies();
    }, [getFreightCompanies])

    /**
     * Opens the full details dialog of free freight eligibility.
     * @type {(function(): void)}
     */
    const openDetailsDialog = useCallback(() => {
        setFreeFreightEligibilityFullDetailsDialogState({
            open: true,
            data: eligibilityState.data,
        });
    }, [eligibilityState.data])

    /**
     * Closes the full details dialog of free freight eligibility.
     * @type {(function(): void)}
     */
    const closeDetailsDialog = useCallback(() => {
        setFreeFreightEligibilityFullDetailsDialogState(initFreeFreightEligibilityFullDetailsDialogState)
        setRefresher(prevState => !prevState);
        refreshExceptions();
    }, [refreshExceptions]);

    /**
     * Handles the selection change event.
     * @type {(function(*): void)}
     */
    const onCustomerSelected = useCallback((_, customer) => {
        setSelectedCustomer(customer);
    }, [])


    /**@type {DataGridRow[]}}*/
    const dataGridRows = useMemo(() => {
        return items.map(item => {
            return ({
                key: item.originalPartId,
                data: item,
                cells: {
                    [FreeShippingFeesManagementOrderSimulationsDataGridColumnNames.product]: item.originalPartNo,
                    [FreeShippingFeesManagementOrderSimulationsDataGridColumnNames.category]: item.originalParentCategoryTitle,
                    [FreeShippingFeesManagementOrderSimulationsDataGridColumnNames.quantity]:
                        <OutlinedInput
                            value={item.quantity}
                            type={'number'}
                            size={'small'}
                            placeholder={'quantity...'}
                            onChange={(quantity) => updateItemQuantity(item.originalPartId, quantity)}
                            inputProps={{min: 0}}
                        />,
                    [FreeShippingFeesManagementOrderSimulationsDataGridColumnNames.price]:
                        <OutlinedInput
                            value={item.price}
                            size={'small'}
                            type={'number'}
                            placeholder={'price...'}
                            onChange={(price) => updateItemPrice(item.originalPartId, price)}
                            inputProps={{min: 0}}
                        />,
                }
            })
        })
    }, [items, updateItemPrice, updateItemQuantity])

    /**
     * Memo version of data grid footer actions
     * @type {DataGridFooterAction[]}
     */
    const footerActions = useMemo(() =>
        /** @type {DataGridFooterAction[]}*/
        [
            {
                name: FreeShippingFeesManagementViewOrderSimulationDataGridActionNames.Delete,
                title: 'Remove',
                type: DataGridActionTypes.delete,
                visibilityConstraints: {
                    minSelectedRows: 1,
                },
            },
        ], [])

    return (
        <>
            <div className={'section'}>
                <div className={'section-paper'}>
                    <p className={'section-title'}>
                        Order Simulation
                    </p>
                    <div className={'w-100 mt-3 d-flex flex-column'}>
                        <OutlinedDropdown
                            className={'mb-3'}
                            label={'Freight Company'}
                            data={freightCompanies?.map(e => ({
                                value: e,
                                label: e.title,
                                id: e.id,
                            })) ?? []}
                            value={freightCompany ?? ''}
                            onChange={setFreightCompany}
                        />
                        <DatePicker
                            className={'mb-3'}
                            label={"Delivery Date"}
                            onChange={(v) => setDeliveryDate(v)}
                            value={deliveryDate}
                        />
                        <CustomerAutocomplete
                            label={'Customer'}
                            value={selectedCustomer}
                            onChange={onCustomerSelected}
                        />
                        {
                            !!selectedCustomer?.id && userAccountInfo &&
                            <div className={'mt-2 d-flex flex-wrap w-100 pl-2'}>
                                <Col xs={12} className={'indicator-item pr-0'}>
                                    <p className={'indicator-label'}>Price Level:</p>
                                    <p className={'indicator-value'}>{userAccountInfo.priceLevel?.title ?? 'N/A'}</p>
                                </Col>
                                <Col xs={12} className={'indicator-item pr-0'}>
                                    <p className={'indicator-label'}>Region:</p>
                                    <p className={'indicator-value'}>{userAccountInfo.region?.title ?? 'N/A'}</p>
                                </Col>
                                <Col xs={12} className={'indicator-item pr-0'}>
                                    <p className={'indicator-label'}>Follows System Rules</p>
                                    <p className={'indicator-value'}>{isSelectedCustomerDetached ? 'No' : "Yes"}</p>
                                </Col>
                            </div>
                        }
                    </div>
                    <Divider className='divider my-3'/>
                    <PartAutocomplete
                        clearOnBlur
                        label={"Add Product"}
                        value={null}
                        onChange={(_, value) => value && addItem(value)}
                        filterOptions={(options) => options.filter(e => !existingPartIds.includes(e.id))}
                    />
                    <PartnerDataGrid
                        ref={dataGridController}
                        {...dataGridSaveConfig}
                        initialLayoutState={{
                            columns: columns,
                        }}
                        hideToolbar
                        hidePagination
                        hidePageSize
                        hideSelectAll
                        footerActions={footerActions}
                        onActionInvoked={onActionInvoked}
                        columns={dataGridColumns}
                        rows={dataGridRows}
                        state={{
                            density: dataGridDensity,
                        }}
                    />
                    <div className={classnames('eligibility-container', {[eligibilityState.state]: true})}>
                        {
                            eligibilityState.state !== eligibilityStates.undetermined &&
                            <>
                                <div className={'d-flex justify-content-center align-items-center w-100 mb-2'}>
                                    <div className={'icon-container'}>
                                        {
                                            eligibilityState.state === eligibilityStates.warning &&
                                            <Close fontSize={'small'} className={'icon'}/>
                                        }
                                        {
                                            eligibilityState.state === eligibilityStates.approved &&
                                            <Done fontSize={'small'} className={'icon'}/>
                                        }
                                        {
                                            eligibilityState.state === eligibilityStates.denied &&
                                            <Close fontSize={'small'} className={'icon'}/>
                                        }
                                    </div>
                                    {
                                        eligibilityState.state === eligibilityStates.warning &&
                                        <p>Incomplete</p>
                                    }
                                    {
                                        eligibilityState.state === eligibilityStates.approved &&
                                        <p>Eligible</p>
                                    }
                                    {
                                        eligibilityState.state === eligibilityStates.denied &&
                                        <p>Not Eligible</p>
                                    }
                                    {
                                        (eligibilityState.state === eligibilityStates.denied || eligibilityState.state === eligibilityStates.approved) &&
                                        <IconButton
                                            disabled={loading}
                                            className={"mx-1 ml-2"}
                                            color={'paper.secondary'}
                                            onPressed={() => setRefresher(prevState => !prevState)}>
                                            {
                                                loading
                                                    ? <CircularProgress size={15} color={'primary'}/>
                                                    : <Refresh className={"icon normal"}/>
                                            }
                                        </IconButton>
                                    }
                                    {
                                        eligibilityState.state === eligibilityStates.denied &&
                                        <IconButton
                                            disabled={loading}
                                            className={"mx-1"}
                                            color={'paper.secondary'}
                                            onPressed={openDetailsDialog}>
                                            {
                                                loading
                                                    ? <CircularProgress size={15} color={'primary'}/>
                                                    : <Info className={"icon normal"}/>
                                            }
                                        </IconButton>
                                    }
                                </div>
                                <ul className={'reasons-list'}>
                                    {
                                        eligibilityState.message?.map((messsage, i) => (
                                            <li key={i}>
                                                {messsage}
                                            </li>
                                        ))
                                    }
                                </ul>
                            </>
                        }
                    </div>
                </div>
            </div>
            <FreeFreightEligibilityFullDetailsDialog
                open={freeFreightEligibilityFullDetailsDialogState.open}
                close={closeDetailsDialog}
                data={freeFreightEligibilityFullDetailsDialogState.data}
                customerId={selectedCustomer?.id}
                customerName={selectedCustomer?.name}
            />
        </>
    )
}

export default FreeShippingFeesManagementViewSettingsOrderSimulation;
